import React, {
  FunctionComponent,
  useState,
  Fragment,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Classes, Dialog,
} from '@blueprintjs/core';
import { ColDef, ColGroupDef } from '@ag-grid-enterprise/all-modules';
import { DistributorConfigurationListenerAfWhiteList } from 'data/distributorConfiguration/distributorConfiguration.types';
import { AppToaster, showError } from 'shared/toaster';
import './edit-allowed-ips-dialog.scss';
import PointsOfInterceptionProperty from '../PointsOfInterceptionProperty';
import AllowedIPsUpdateTable from '../AllowedIPsUpdateTable';

type Props = {
  isEditing: boolean,
  isOpen: boolean,
  ipsWhiteList: DistributorConfigurationListenerAfWhiteList[],
  ipTableColumnDefs: (ColDef | ColGroupDef)[],
  distributorName?: string;
  listeningIpAddress?: string;
  listeningPort?: string;
  onSubmit: (data: DistributorConfigurationListenerAfWhiteList[]) => void,
  onCancel: () => void,
}

const EditAllowedIPsDialog: FunctionComponent<Props> = ({
  isOpen,
  distributorName,
  ipsWhiteList,
  ipTableColumnDefs,
  listeningIpAddress,
  listeningPort,
  isEditing,
  onCancel,
  onSubmit,
}: Props) => {
  const [ipsWhiteListChanged, setIpsWhiteListChanged] = useState(false);
  const [isDialogValid, setDialogValid] = useState(true);
  const [isDialogSubmitted, setDialogSubmitted] = useState(false);
  const [
    currentAFWhiteListData,
    setCurrentAFWhiteListData,
  ] = useState<DistributorConfigurationListenerAfWhiteList[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    setIpsWhiteListChanged(false);
    return () => {
      AppToaster.clear();
    };
  }, [isOpen]);

  const handleCancelAddNewIp = () => {
    setIpsWhiteListChanged(true);
  };

  const handleCellKeyDown = () => {
    setIpsWhiteListChanged(true);
  };

  const handleRowDataUpdated = (
    data: DistributorConfigurationListenerAfWhiteList[],
    { hasAddressFormatError, hasNonUniqueAFError }: { hasAddressFormatError: boolean, hasNonUniqueAFError: boolean },
  ) => {

    AppToaster.clear();
    if (hasNonUniqueAFError) {
      showError(t('allowedIps.error.mustBeUnique'));
    }

    if (hasAddressFormatError || hasNonUniqueAFError) {
      setDialogValid(false);

    } else {
      setCurrentAFWhiteListData(data);
      setIpsWhiteListChanged(true);
      setDialogValid(true);
    }
  };

  const handleSubmit = () => {
    setDialogSubmitted(true);
    if (isDialogValid) {
      setIpsWhiteListChanged(false);
      onSubmit(currentAFWhiteListData);
    }
  };

  const handleCancel = () => {
    setIpsWhiteListChanged(false);
    onCancel();
    setDialogSubmitted(false);
  };

  const handleIsGrayUpdate = () => {
    setIpsWhiteListChanged(true);
  }

  const renderFooterActions = (hasChanged: boolean) => {
    if (hasChanged && isEditing) {
      return (
        <Fragment>
          <Button data-testid="EditAllowedIPsDialogCancelButton" onClick={handleCancel}>{t('common.button.cancel')}</Button>
          <Button data-testid="EditAllowedIPsDialogConfirmButton" intent="primary" onClick={handleSubmit}>{t('common.button.confirm')}</Button>
        </Fragment>
      );
    }

    return <Button intent="primary" onClick={handleCancel}>{t('common.button.close')}</Button>;
  };

  return (
    <Dialog
      autoFocus
      isOpen={isOpen}
      title={t('allowedIps.title')}
      className="edit-allowed-ips-dialog"
      onClose={handleCancel}
    >
      <div className={Classes.DIALOG_BODY}>
        <div className="edit-allowed-ips-dialog-points-of-interception-properties">
          <PointsOfInterceptionProperty label={t('allowedIps.distributorConfiguration')} value={distributorName} />
          <PointsOfInterceptionProperty label={t('allowedIps.listeningIp')} value={listeningIpAddress} />
          <PointsOfInterceptionProperty label={t('allowedIps.listeningPort')} value={listeningPort} />
        </div>
        <div>
          <p><strong>{t('allowedIps.accessFunctionList')}</strong></p>
          <p>
            {t('allowedIps.description1')}
            <br />
            {t('allowedIps.description2')}
          </p>
        </div>
        <div>
          <AllowedIPsUpdateTable
            isEditing={isEditing}
            isDialogSubmitted={isDialogSubmitted}
            rowData={ipsWhiteList}
            ipTableColumnDefs={ipTableColumnDefs}
            onAddNewIp={handleCancelAddNewIp}
            onRowDataUpdated={handleRowDataUpdated}
            onCellKeyDown={handleCellKeyDown}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            {renderFooterActions(ipsWhiteListChanged)}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default EditAllowedIPsDialog;
